/* eslint-disable @typescript-eslint/no-explicit-any */
// In src/components/Navbar.tsx
import { useState, useRef, useEffect, type MouseEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDarkMode } from "../context/DarkModeContext";
import { useMobileFeatures } from "../hooks/useMobileFeatures";
import {
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Menu,
  X,
  User,
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  Search,
  Bell,
  Settings,
} from "lucide-react";
import logo from "/findwork.jpg";
import SearchBox from "./SearchBox";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const { user, logout } = useAuth();
  const { isDark, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const { isNative, isOnline } = useMobileFeatures();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside as any);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside as any);
  }, []);

  const handleFindJobClick = (e: React.MouseEvent) => {
    if (!user) {
      e.preventDefault();
      navigate("/packages");
      setOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
    setUserMenuOpen(false);
    navigate("/");
  };

  const isActivePath = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname.startsWith("/dashboard");
    }
    return location.pathname === path;
  };

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/packages", label: "Packages" },
    { path: "/find-job", label: "Find Jobs" },
    { path: "/community", label: "Community" },
  ];

  const socialLinks = [
    {
      icon: Facebook,
      url: "https://www.facebook.com/profile.php?id=61584796086016",
      label: "Facebook",
    },
    {
      icon: Twitter,
      url: "https://x.com/home",
      label: "Twitter",
    },
    {
      icon: Linkedin,
      url: "http://www.linkedin.com/in/find-work-261a53393",
      label: "LinkedIn",
    },
    {
      icon: Instagram,
      url: "https://www.instagram.com/findworkke/",
      label: "Instagram",
    },
  ];

  // Helper function to handle disabled state for links
  // const handleDisabledLink = (e: MouseEvent<HTMLAnchorElement>, isDisabled: boolean) => {
  //   if (isDisabled) {
  //     e.preventDefault();
  //   }
  // };

  return (
    <nav
      className={`shadow-lg sticky top-0 z-50 transition-colors duration-300 ${
        isDark
          ? "bg-gray-900 border-b border-gray-700"
          : "bg-white border-b border-gray-200"
      } ${isNative ? "mobile-nav" : "desktop-nav"}`}
    >
      {/* Mobile Status Bar - Only shown in native mobile apps */}
      {isNative && (
        <div className="mobile-status-bar">
          {!isOnline && (
            <div className="w-full bg-red-500 text-white text-xs text-center py-1">
              <span className="offline-indicator">Offline</span>
            </div>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo + Brand */}
          <div className="flex items-center space-x-3">
            <Link
              to="/"
              className="flex items-center space-x-3 group"
              onClick={() => setOpen(false)}
            >
              <img
                src={logo}
                alt="FindWork"
                className="h-10 w-10 rounded-full border-2 border-sky-500 group-hover:border-sky-400 transition-colors duration-300"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold bg-gradient-to-r from-sky-500 to-sky-600 bg-clip-text text-transparent">
                  FindWork
                </span>
                <div className="text-xs text-gray-500 -mt-1 hidden sm:block">
                  <span className="block">Hire Local Workers Fast</span>
                  <span className="block">Get Matched in Hours</span>
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={
                  link.path === "/find-job" && !user ? "/packages" : link.path
                }
                onClick={
                  link.path === "/find-job" ? handleFindJobClick : undefined
                }
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 relative group ${
                  isActivePath(link.path)
                    ? "text-sky-600 dark:text-sky-400"
                    : "text-gray-600 dark:text-gray-300 hover:text-sky-600 dark:hover:text-sky-400"
                }`}
              >
                {link.label}
                {isActivePath(link.path) && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-sky-500 rounded-full"></span>
                )}
              </Link>
            ))}
          </div>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Online Status Indicator for Desktop Native Apps */}
            {isNative && !isOnline && (
              <div className="text-red-500 text-xs font-medium mr-2">
                Offline
              </div>
            )}

            {/* Search Toggle Button */}
            <button
              ref={searchButtonRef}
              onClick={() => setSearchExpanded(!searchExpanded)}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                isDark
                  ? "text-gray-400 hover:text-sky-400 hover:bg-gray-800"
                  : "text-gray-500 hover:text-sky-600 hover:bg-gray-100"
              } ${isNative && !isOnline ? "opacity-50 cursor-not-allowed" : ""}`}
              aria-label="Search jobs"
              disabled={isNative && !isOnline}
            >
              <Search size={20} />
            </button>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => toggleDarkMode(!isDark)}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                isDark
                  ? "text-yellow-400 hover:bg-gray-800 hover:text-yellow-300"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
              }`}
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Social Links - Hide in offline mode for native apps */}
            {(!isNative || isOnline) && (
              <div className="flex items-center space-x-2 border-l border-gray-300 dark:border-gray-600 pl-4 ml-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-2 rounded-lg transition-colors duration-300 ${
                      isDark
                        ? "text-gray-400 hover:text-sky-400 hover:bg-gray-800"
                        : "text-gray-500 hover:text-sky-600 hover:bg-gray-100"
                    }`}
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            )}

            {/* Auth Section */}
            {user ? (
              <div
                className="flex items-center space-x-3 ml-2"
                ref={userMenuRef}
              >
                {/* Notifications */}
                <button
                  className={`p-2 rounded-lg transition-colors duration-300 relative ${
                    isDark
                      ? "text-gray-400 hover:text-sky-400 hover:bg-gray-800"
                      : "text-gray-500 hover:text-sky-600 hover:bg-gray-100"
                  } ${isNative && !isOnline ? "opacity-50 cursor-not-allowed" : ""}`}
                  aria-label="Notifications"
                  disabled={isNative && !isOnline}
                >
                  <Bell size={20} />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900"></span>
                </button>

                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() =>
                      !(isNative && !isOnline) && setUserMenuOpen(!userMenuOpen)
                    }
                    className={`flex items-center space-x-3 p-2 rounded-lg transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                      isNative && !isOnline
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={isNative && !isOnline}
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {user.name?.charAt(0).toUpperCase() || "U"}
                    </div>
                    <div className="text-left hidden lg:block">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name || "User"}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {user.role || "user"}
                      </p>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`text-gray-500 transition-transform duration-300 ${
                        userMenuOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.name || "User"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email || "No email"}
                        </p>
                      </div>

                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      >
                        <User size={16} />
                        <span>Dashboard</span>
                      </Link>

                      <Link
                        to="/dashboard/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                      >
                        <Settings size={16} />
                        <span>Profile & Settings</span>
                      </Link>

                      <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200"
                        >
                          <LogOut size={16} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3 ml-4">
                <Link
                  to="/login"
                  onClick={(e) => {
                    if (isNative && !isOnline) {
                      e.preventDefault();
                      return;
                    }
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 border ${
                    isDark
                      ? "border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white"
                      : "border-sky-500 text-sky-600 hover:bg-sky-500 hover:text-white"
                  } ${isNative && !isOnline ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={(e) => {
                    if (isNative && !isOnline) {
                      e.preventDefault();
                      return;
                    }
                  }}
                  className={`px-4 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-sky-600 text-white font-medium hover:from-sky-600 hover:to-sky-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                    isNative && !isOnline
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Online Status Indicator for Mobile */}
            {isNative && !isOnline && (
              <div className="text-red-500 text-xs font-medium mr-1">
                Offline
              </div>
            )}

            {/* Search Button for Mobile */}
            <button
              ref={searchButtonRef}
              onClick={() => setSearchExpanded(!searchExpanded)}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                isDark
                  ? "text-gray-400 hover:text-sky-400 hover:bg-gray-800"
                  : "text-gray-500 hover:text-sky-600 hover:bg-gray-100"
              } ${isNative && !isOnline ? "opacity-50 cursor-not-allowed" : ""}`}
              aria-label="Search jobs"
              disabled={isNative && !isOnline}
            >
              <Search size={20} />
            </button>

            {/* Dark Mode Toggle for Mobile */}
            <button
              onClick={() => toggleDarkMode(!isDark)}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                isDark
                  ? "text-yellow-400 hover:bg-gray-800"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <button
              onClick={() => setOpen(!open)}
              className={`p-2 rounded-lg transition-colors duration-300 ${
                isDark
                  ? "text-gray-400 hover:text-sky-400 hover:bg-gray-800"
                  : "text-gray-500 hover:text-sky-600 hover:bg-gray-100"
              }`}
              aria-label="Toggle menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Expanded Search Box */}
        {searchExpanded && (
          <div className="pb-4">
            <SearchBox />
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {open && (
        <div
          className={`md:hidden absolute top-16 left-0 right-0 shadow-xl border-b ${
            isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          } transition-all duration-300 z-30`}
        >
          <div className="px-4 pt-4 pb-6 space-y-4">
            {/* Offline Warning in Mobile Menu */}
            {isNative && !isOnline && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                ⚠️ You're currently offline. Some features may be unavailable.
              </div>
            )}

            {/* Mobile Navigation Links */}
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={
                  link.path === "/find-job" && !user ? "/packages" : link.path
                }
                onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                  if (link.path === "/find-job") handleFindJobClick(e);
                  if (isNative && !isOnline && link.path !== "/") {
                    e.preventDefault();
                    alert("This feature requires an internet connection");
                    return;
                  }
                  setOpen(false);
                }}
                className={`block px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isActivePath(link.path)
                    ? "bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile User Info */}
            {user && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-12 h-12 bg-gradient-to-r from-sky-500 to-sky-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                    {user.name?.charAt(0).toUpperCase() || "U"}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user.name || "User"}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                      {user.role || "user"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Link
                    to="/dashboard"
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                      if (isNative && !isOnline) {
                        e.preventDefault();
                        alert("Dashboard requires internet connection");
                        return;
                      }
                      setOpen(false);
                    }}
                    className={`px-4 py-3 text-center bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 transition-colors duration-300 ${
                      isNative && !isOnline
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : ""
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/dashboard/profile"
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                      if (isNative && !isOnline) {
                        e.preventDefault();
                        alert("Profile requires internet connection");
                        return;
                      }
                      setOpen(false);
                    }}
                    className={`px-4 py-3 text-center border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-300 ${
                      isNative && !isOnline
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : ""
                    }`}
                  >
                    Profile
                  </Link>
                </div>
              </div>
            )}

            {/* Mobile Auth Buttons */}
            {!user && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <Link
                  to="/login"
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    if (isNative && !isOnline) {
                      e.preventDefault();
                      alert("Login requires internet connection");
                      return;
                    }
                    setOpen(false);
                  }}
                  className={`block px-4 py-3 text-center border border-sky-500 text-sky-600 dark:text-sky-400 rounded-lg font-medium hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors duration-300 ${
                    isNative && !isOnline
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    if (isNative && !isOnline) {
                      e.preventDefault();
                      alert("Signup requires internet connection");
                      return;
                    }
                    setOpen(false);
                  }}
                  className={`block px-4 py-3 text-center bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-sky-700 transition-all duration-300 shadow-lg ${
                    isNative && !isOnline
                      ? "opacity-50 cursor-not-allowed pointer-events-none"
                      : ""
                  }`}
                >
                  Get Started Free
                </Link>
              </div>
            )}

            {/* Mobile Social Links - Only show when online for native apps */}
            {(!isNative || isOnline) && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-center space-x-6">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 transition-colors duration-300 ${
                        isDark
                          ? "text-gray-400 hover:text-sky-400"
                          : "text-gray-500 hover:text-sky-600"
                      }`}
                      aria-label={social.label}
                    >
                      <social.icon size={20} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
